import { api } from '../lib/api.js';
import { identifyTrail } from '../lib/stationTrail.js';
import type { RevisionPending } from './faults.js';

/**
 * חלק בן של הרכבה שעדיין לא הגיע, והשלב שהוא תקוע בו.
 *
 * ⚠ נכתב פעם אחת כאן ומיוצא לכל מסך שמסמן „חלקים חסרים" (קיוסק, עמדה, לוח
 * ייצור, עץ המוצר). השרת מגדיר אותו ב-`scan/assembly-children.ts`, ושתי
 * הגדרות של אותו מטען הן בדיוק איך ששדה נשמט בדרך ([[edal-client-type-drops-fields]]).
 */
export type MissingChild = {
  partId: string;
  partNumber: string;
  stageName: string;
  sequenceIndex: number;
  unitsCompleted: number;
  target: number;
};

/** תשובת „רענן לרוויזיה החדשה" — ראה `ScanService.switchRevision`. */
export type SwitchRevisionResponse = {
  ok: true;
  closed: { unitsCompleted: number; isComplete: boolean; minutesReported: number; stageName: string; partNumber: string };
  /** השעון החדש על הרוויזיה החדשה — null כשלא נפתח (ראה `notStarted`). */
  newSession: {
    sessionId: string; partNumber: string; stageName: string; sequenceIndex: number;
    startedAt: string; partVersion: number; revision: string | null;
  } | null;
  notStarted: { reason: 'STAGE_COMPLETE' } | { reason: 'PRODUCTION_HOLD'; text: string } | null;
  fromRevision: string | null;
  toRevision: string | null;
};

// The public kiosk queue returns only display fields (not the full dispatch
// payload) — see /api/scan/queue. Keep in sync with that handler.
export type KioskQueueRow = {
  partId: string;
  partNumber: string;
  partName: string | null;
  projectName: string | null;
  dueDate: string | null;
};

export type EmployeeDto = {
  id: string; employeeNumber: string; name: string;
  photoUrl: string | null;
  department: { id: string; name: string } | null;
  departments: { id: string; name: string }[];
  /**
   * `he` / `ru` — השפה שהקיוסק עובר אליה ברגע שהתג נקרא.
   *
   * ⚠ לקח „Client types drop fields": השרת החזיר את זה, ואם הטיפוס כאן לא
   * מצהיר עליו — אף מסך לא יכול לקרוא אותו, והשדה נשאר מת בדיוק כפי שהיה
   * מת בעמודה. ראה `useKioskLanguage`.
   */
  preferredLanguage: string;
};

export type GreetingPart = 'MORNING' | 'NOON' | 'EVENING';

export type BadgeResponse = {
  employee: EmployeeDto;
  /** U-1009-09: true only on the employee's first scan of the Israel day. */
  greetToday?: boolean;
  greeting?: GreetingPart;
  openSessions: { id: string; partNumber: string; stageName: string; startedAt: string }[];
};

export type ContinueOwnStageResponse = {
  ok: true;
  closed: { unitsCompleted: number; isComplete: boolean; minutesReported: number; stageName: string; partNumber: string };
  newSession: { sessionId: string; partNumber: string; stageName: string; sequenceIndex: number; startedAt: string } | null;
  notStarted: { reason: 'STAGE_COMPLETE' } | { reason: 'PRODUCTION_HOLD'; text: string } | { reason: 'EARLIER_INCOMPLETE'; stageName: string } | null;
};

export type StartResponse = {
  sessionId: string; partNumber: string; stageName: string;
  sequenceIndex: number; outOfOrder: boolean; startedAt: string;
  /** „מחוץ למחלקה" — עובד ממחלקה אחרת, בלי אישור מנהל. מסומן, לא נחסם (U-0924-08). */
  outOfDepartment?: boolean;
  /** „אני אבצע את השלב הקודם": השלב של העובד שאליו יעבור בסיום. */
  selfPriorForSeq?: number | null;
  /**
   * Competences this department's work needs that the worker does not hold, or
   * that have expired (ISO 9001 clause 7.2). A WARNING — the session is already
   * open by the time this arrives, and nothing here refuses the work.
   */
  competenceWarnings?: string[];
  /** אלי 16/10/2026: השלב הקודם לא דיווח דבר — בסיום יידרש קוד מנהל. אזהרה בלבד. */
  priorEmpty?: { stageName: string } | null;
};

/**
 * „לאן החלק עובר עכשיו" — אלי, 20/09/2026.
 *
 * STAGE = השלב הבא במסלול של החלק עצמו · PARENT = מצטרף להרכבה שמעליו ·
 * NONE = אין המשך, ואז העמדה **אינה ממציאה יעד**: „מחסן" שנכתב בניחוש שולח
 * חלק למקום שבו איש לא מחפש אותו. הכלל עצמו ב-`scan/next-destination.ts`.
 */
export type Handoff = {
  kind: 'STAGE' | 'PARENT' | 'NONE';
  departmentId: string | null;
  departmentName: string | null;
  stageName: string | null;
  parentPartNumber: string | null;
  parentPartName: string | null;
};

export type EndResponse = {
  ok: true;
  unitsCompleted: number;
  isComplete: boolean;
  /** השלב הושלם כשחסרים חלקי בן — התרעה, לא חסימה (אלי 29/09/2026). */
  missingChildren?: string | null;
  /** השלב הקודם לא דיווח — הדיווח נשמר, ומנהל הייצור יאשר את השלב (03/11/2026). */
  priorUnreported?: string | null;
  minutesReported: number;
  startedAt: string;
  endedAt: string;
  stageName: string;
  partNumber: string;
  handoff: Handoff;
};

export type StateResponse =
  | {
      mode: 'end'; session: { id: string; stageName: string; startedAt: string; remainingUnits: number }; forwardJump: false;
      /**
       * השעון רץ על רוויזיה שהוחלפה — „כמה סיימת לפי הקודמת", ופיצול
       * (`switchRevision`) במקום סיום. אלי, 24/09/2026.
       */
      revisionPending?: RevisionPending | null;
    }
  | {
      mode: 'start';
      forwardJump: boolean;
      // ── „חלקים חסרים" — סימון, לא שער (27/09/2026) ─────────────────────
      //
      // High #12 הוסיף את השדה כדי שהקיוסק יבקש קוד מנהל מראש. אלי הכריע
      // „לאפשר חופשי, עם סימון „חלקים חסרים"", ולכן הקיוסק פותח את השרטוט
      // ומציג את הסימון. `assemblyMissing` נושא את שמות החלקים — „חסרים
      // חלקים" בלי לומר אילו אינו משפט שאפשר לפעול לפיו.
      assemblyBlocked?: boolean;
      assemblyMissing?: MissingChild[];
      // Why this part may not be worked at all, decided by the same gate that
      // would refuse the start. Present means blocked; the drawing does not
      // open and the reason is shown instead.
      blocked?: { kind: string; reason: string } | null;
      stagePreview?: { sequenceIndex: number; stageName: string };
      /** The stage's department — what the machine picker needs. */
      departmentId?: string | null;
      /** True when that department has a machine somebody must pick. */
      needsMachine?: boolean;
      deptMissing?: true;
      deptMismatch?: true;
      /**
       * הערות איכות שהמשרד כתב על החלק, ואם **העובד הסורק** כבר אישר.
       *
       * אלי, 23/09/2026: „ברגע שהעובד סורק תציג את ההערה לאישור העובד."
       * ⚠ אופציונלי כי שני הענפים המוקדמים (`deptMissing`/`deptMismatch`)
       * חוזרים לפני שהחלק נפתר במלואו.
       */
      qualityNotes?: Array<{
        id: string; body: string; byName: string | null;
        createdAt: string; acknowledged: boolean;
      }>;
    };

// The no-badge fallback list. Deliberately carries NO employee number — it is
// rendered on a shared kiosk screen, and the number is what a badge proves.
export type IdentifiableEmployee = { id: string; name: string; photoUrl: string | null; departmentName: string | null };

// מי עובד עכשיו במחלקה — הלוח של קיוסק הגמר.
//
// אין כאן `employeeNumber` ולא יהיה: זה מסך משותף בקומה, והמספר הוא מה
// שהתג מוכיח. השם הוא מה שאדם שעומד מול המסך צריך.
//
// כל שדה שהשרת שולח מופיע כאן. טיפוס לקוח שמשמיט שדה אינו שגיאת הידור —
// הוא פשוט מעלים אותו מהמסך בשקט, וזה באג שכבר חזר כאן.
export type DepartmentSessionRow = {
  sessionId: string;
  workerName: string;
  employeeId: string;
  partId: string;
  partNumber: string;
  partName: string | null;
  stageName: string;
  sequenceIndex: number;
  startedAt: string;
  elapsedMinutes: number;
  /** כמה נדרשים, וכמה כבר הושלמו בשלב הזה — „5 מתוך 40". */
  quantity: number;
  unitsDone: number;
  // ── מה שכרטיסיית החלק בקיוסק הגמר צריכה ─────────────────────────────
  //
  // אלי, 22/09/2026: „כרטיסיות חלקים… עם תמונה מתוך ה-PDF של השרטוט.
  // ניתן לפתוח את השרטוט משם."
  //
  // שניהם מהגרסה **הפעילה**. `null` = אין גרסה פעילה, והכרטיסייה אומרת
  // „אין שרטוט פעיל" במקום להציג מסגרת ריקה או כתובת שבורה.
  thumbnailUrl: string | null;
  drawingUrl: string | null;
  projectId: string;
  projectNumber: string;
  /** תמונת העובד — „שירגיש שייכות, שהוא זה שמבצע". אלי, 22/09/2026. */
  workerPhotoUrl: string | null;
  /**
   * צבע הכותרת: ירוק עובד, **אדום אי-התאמה**.
   *
   * ⚠ ובלי כתום („חורג מהזמן"), בהכרעת אלי אחרי מדידה: אין אף סטנדרט זמן
   * במערכת — 110 שלבים, 73 חלקים, אפס. מצב שנראה ולא נדלק לעולם הוא גרוע
   * ממצב שאינו קיים.
   */
  hasOpenNc: boolean;
  /** „מחוץ למחלקה" — עובד ממחלקה אחרת, בלי אישור מנהל. מסומן, לא נחסם (U-0924-08). */
  outOfDepartment?: boolean;
  /**
   * הגרסה שהעבודה נפתחה עליה, מול זו שהחלק נמצא בה עכשיו.
   *
   * אלי, 23/09/2026: „אם הוחלפה רוויזיה או חלק אתה תציג את זה בבירור."
   * ⚠ עובד שמחזיק שרטוט ישן ביד אינו יכול לדעת שהוחלף — המערכת ידעה
   * ושתקה.
   */
  startedOnVersion: number;
  /**
   * ⚠ **אות** הרוויזיה שהעבודה נפתחה עליה. הכרטיס הדפיס „עבדת לפי 3,
   * העדכנית B" — מספר גרסה פנימי ליד אות. null = התחיל בלי שרטוט.
   */
  startedOnRevision: string | null;
  currentVersion: number;
  revisionChanged: boolean;
  currentRevision: string | null;
  /**
   * הערות איכות שהמשרד כתב על החלק — ואם **העובד הזה** כבר אישר.
   *
   * אלי, 23/09/2026: „ברגע שהעובד סורק תציג את ההערה לאישור העובד."
   */
  qualityNotes: Array<{
    id: string; body: string; byName: string | null;
    createdAt: string; acknowledged: boolean;
  }>;
};

/** חלק עם אי-התאמה פתוחה במחלקה — נשאר על הלוח גם בלי סשן. */
export type DepartmentNcRow = {
  partId: string; partNumber: string; partName: string | null;
  projectNumber: string; stageName: string;
  faultWhat: string; openedAt: string; thumbnailUrl: string | null;
  /** חוסמת את המחלקה הזאת (קטגוריה חוסמת, אחרי שלב הדיווח, בלי תשובת הנדסה) — 21/10/2026 */
  blocking?: boolean;
};

// U-1009-09 — every kiosk identifies through scanApi.badge/identify, so this is
// the one place that hands a first-of-the-day scan to <GreetingSplash/>.
function announceGreeting(res: BadgeResponse): BadgeResponse {
  if (res.greetToday) {
    try {
      window.dispatchEvent(new CustomEvent('edal:greet', { detail: { employee: res.employee, greeting: res.greeting } }));
    } catch { /* greeting is cosmetic */ }
  }
  return res;
}

/** איפה עומד הקיוסק — `kiosk: 'laser'` או מחלקה. בלי אף אחד: הרשימה כבויה. */
export type PickWhere = { departmentId?: string; kiosk?: 'laser' };
const pickQuery = (w: PickWhere): string => new URLSearchParams(Object.entries(w).filter(([, v]) => v) as [string, string][]).toString();

export const scanApi = {
  // `coworker` — תג של שותף שנוסף לשעון: אינו כניסה לעמדה (identify-log.ts).
  badge: (payload: string, opts: { coworker?: boolean } = {}) =>
    api<BadgeResponse>('/api/scan/badge', {
      method: 'POST', body: { payload, ...(opts.coworker ? { coworker: true } : identifyTrail()) },
    }).then(announceGreeting),
  // „בחר מהרשימה" רק במחלקה שהופעל בה (אלי 16/10/2026).
  photoPick: (where: PickWhere) => api<{ enabled: boolean }>(`/api/scan/photo-pick?${pickQuery(where)}`),
  employees: (where: PickWhere) => api<IdentifiableEmployee[]>(`/api/scan/employees?${pickQuery(where)}`),
  departments: () => api<{ id: string; name: string; nameRu: string | null }[]>('/api/scan/station/departments'),
  departmentSessions: (departmentId: string) =>
    api<DepartmentSessionRow[]>(`/api/scan/station/department-sessions?departmentId=${encodeURIComponent(departmentId)}`),
  identify: (employeeId: string, where: PickWhere) =>
    api<BadgeResponse>('/api/scan/identify', { method: 'POST', body: { employeeId, ...where, ...identifyTrail() } }).then(announceGreeting),
  state: (employeeNumber: string, partPayload: string) =>
    api<StateResponse>(`/api/scan/state?employeeNumber=${encodeURIComponent(employeeNumber)}&partPayload=${encodeURIComponent(partPayload)}`),
  start: (body: {
    primaryEmployeeNumber: string; coWorkerNumbers?: string[]; partPayload: string;
    /** קוד אישור אישי של מנהל. The username+password pair was a second door to the system password. */
    approvalCode?: string; departmentId?: string;
    /** Which machine the work is on (ISO 9001 §7.1.3). Required where the department has any. */
    machineId?: string;
    /** „אני אבצע את השלב הקודם" — השלב הקודם שהעובד מבצע בעצמו. */
    performPriorSeq?: number;
    /**
     * עבודה במקביל, עד 4 שרטוטים (אלי 30/09) — רק מעמדת העובד, וכשכבר יש לו
     * עבודה פתוחה. בלעדיו `OTHER_SESSION_OPEN` נשאר כמו היום (שאר הקיוסקים).
     */
    parallel?: boolean;
    /** רתך פתח בן בלי ריתוך — השעון נפתח על ריתוך האבא (אלי 03/10/2026). */
    viaChildId?: string;
    /** „המשך בלי שרטוט" — העובד בחר (אלי 21/10/2026). */
    continueWithoutDrawing?: boolean;
  }) => api<StartResponse>('/api/scan/start', { method: 'POST', body }),
  /** „שכחתי לעבור — סיימתי גם את השלב שלי": השעון נחתך לפי הדקות של השלב הקודם. */
  /** „מה ביצעת" בחלון הסיום — השעון עובר למחלקה שנבחרה (אלי 04/10, U-1004-05). */
  retarget: (body: { sessionId: string; employeeNumber: string; departmentId: string; approvalCode?: string; machineId?: string }) =>
    api<StartResponse>('/api/scan/station/retarget', { method: 'POST', body }),
  finishBothStages: (body: { sessionId: string; employeeNumber: string; priorUnits: number; priorMinutes: number; ownUnits: number; approvalCode?: string }) =>
    api<ContinueOwnStageResponse & { own: EndResponse | null }>('/api/scan/station/finish-both-stages', { method: 'POST', body }),
  /** „סיימתי את השלב הקודם — עובר לשלב שלי": סוגר ופותח שעון בפעולה אחת. */
  continueOwnStage: (body: { sessionId: string; units: number; employeeNumber: string; approvalCode?: string; scrapUnits?: number }) =>
    api<ContinueOwnStageResponse>('/api/scan/station/continue-own-stage', { method: 'POST', body }),
  end: (body: { sessionId: string; units: number; employeeNumber: string; approvalCode?: string; scrapUnits?: number }) =>
    api<EndResponse>('/api/scan/end', { method: 'POST', body }),
  /**
   * „סיים ודווח כמות" מכרטיס בלוח הגמר — בלי תג. השרת לוקח את הבעלים
   * מהסשן; מספרי התג אינם יוצאים מהשרת (ביקורת 30/08).
   */
  endFromBoard: (body: { sessionId: string; units: number; approvalCode?: string; scrapUnits?: number }) =>
    api<EndResponse>('/api/scan/end-from-board', { method: 'POST', body }),
  /**
   * „רענן לרוויזיה החדשה" — השעון על הקודמת נסגר עם מה שדווח, וחדש נפתח
   * על החדשה (עם השותפים). אלי, 24/09/2026: „השעון רץ; האישור מפצל."
   */
  switchRevision: (body: { sessionId: string; units: number; employeeNumber: string; approvalCode?: string; scrapUnits?: number }) =>
    api<SwitchRevisionResponse>('/api/scan/station/switch-revision', { method: 'POST', body }),
  /** אותו פיצול מכרטיס בלוח הגמר — בלי תג, כמו `endFromBoard`. */
  switchRevisionFromBoard: (body: { sessionId: string; units: number; approvalCode?: string; scrapUnits?: number }) =>
    api<SwitchRevisionResponse>('/api/scan/switch-revision-from-board', { method: 'POST', body }),
  cancel: (sessionId: string, employeeNumber: string) =>
    api<{ ok: true }>('/api/scan/cancel', { method: 'POST', body: { sessionId, employeeNumber } }),
  // Close an open session + declare the previous-stage incoming quantity, then
  // start work — gated by the manager PIN.
  reconcileClose: (body: {
    sessionId: string; employeeNumber: string; partPayload: string; departmentId: string;
    priorUnits: number | null; managerCode: string;
    /** המכונה של השעון החדש — כמו בהתחלה רגילה. */
    machineId?: string | null;
  }) => api<{
    sessionId: string; partNumber: string; stageName: string; sequenceIndex: number;
    startedAt: string; priorStageName: string | null; priorUnitsSet: number | null;
  }>('/api/scan/station/reconcile-close', { method: 'POST', body }),
  // ההעדפות שמסך קיוסק צריך לפני שמישהו התחבר. `laserHeavyPartKg` כאן ולא
  // ב-/api/settings כי מסך המדבקות חי בעמדת הלייזר — בלי התחברות.
  settings: () => api<{
    kioskIdleSeconds: number; allowCoWorkers: boolean; maxCoWorkers: number;
    laserHeavyPartKg: number; onboardingMode: boolean; onboardingSince: string | null;
    /** מחלקת הלייזר — שלב שלה מדווח באקסל בלבד (`stageReportsByExcelOnly`). */
    laserDepartmentId?: string | null;
  }>('/api/scan/settings'),
  departmentNc: (departmentId: string) =>
    api<DepartmentNcRow[]>(`/api/scan/station/department-nc?departmentId=${encodeURIComponent(departmentId)}`),
  /** „נרשם מי ומתי" — אישור קריאה של הערת איכות, בתג. */
  ackNote: (noteId: string, employeeNumber: string) =>
    api<{ acknowledgedAt: string }>(`/api/part-notes/${encodeURIComponent(noteId)}/ack`,
      { method: 'POST', body: { employeeNumber } }),
  queue: (departmentId: string) =>
    api<KioskQueueRow[]>(`/api/scan/queue?departmentId=${encodeURIComponent(departmentId)}`),
};
